import {
    BaseControl,
    ToggleControl,
    Button,
} from '@wordpress/components';
import { __ } from '@wordpress/i18n';
import { FIELD_TYPES } from '../../constants/fieldTypes';

import { SelectControl, TextControl } from '../../../components/fieldComponents';

const generateMetaKey = (label) => {
    return label
        .toLowerCase()
        .replace(/[^a-z0-9_]+/g, '_')
        .replace(/^_+|_+$/g, '');
};

const GeneralSettingsTab = ({ field, onChange, onRemove, tabs = [] }) => {
    const handleChange = (key, value) => onChange({ ...field, [key]: value });

    const handleLabelBlur = () => {
        if (!field.field_key?.trim()) {
            handleChange('field_key', generateMetaKey(field.label || ''));
        }
    };

    return (
        <>
            <SelectControl
                field={{
                    label: __('Field Type', 'voxycure-framework'),
                    options: FIELD_TYPES
                }}
                value={field.type}
                onChange={(value) => handleChange('type', value)}
            />

            <TextControl
                field={{
                    label: __('Field Label', 'voxycure-framework'),
                    type: 'text'
                }}
                value={field.label || ''}
                onChange={(value) => handleChange('label', value)}
                onBlur={handleLabelBlur}
                help={__('This text will appear on the form.', 'voxycure-framework')}
            />

            <TextControl
                field={{
                    label: __('Field Name', 'voxycure-framework'),
                    type: 'text'
                }}
                value={field.field_key || ''}
                onChange={(value) =>
                    handleChange(
                        'field_key',
                        value.toLowerCase().replace(/[^a-z0-9_]/g, '')
                    )
                }
                help={__('Unique identifier. Lowercase letters, numbers, and underscores only.', 'voxycure-framework')}
            />

            {['select', 'multi_select', 'radio', 'checkbox'].includes(field.type) && (
                <FieldOptions
                    options={field.options || []}
                    onChange={(optionList) => handleChange('options', optionList)}
                />
            )}

            {['image', 'gallery'].includes(field.type) && (
                <SelectControl
                    field={{
                        label: __('Image Size', 'voxycure-framework'),
                        options: (window.VoxyFrameAdmin?.imageSizes || []).map(size => ({
                            label: size.label,
                            value: size.value
                        }))
                    }}
                    value={field.image_size || ''}
                    onChange={(value) => handleChange('image_size', value)}
                    help={__('Choose the image size to use when displaying the image.', 'voxycure-framework')}
                />
            )}

            {field.type === 'range_slider' && (
                <div className='voxy-flex'>
                    <TextControl
                        field={{
                            label: __('Minimum Value', 'voxycure-framework'),
                            type: 'number',
                        }}
                        value={field.range_config?.min || ''}
                        onChange={(value) =>
                            handleChange('range_config', {
                                ...field.range_config,
                                min: Number(value),
                            })
                        }
                        help={__('Lowest allowed value for the range slider.', 'voxycure-framework')}
                    />

                    <TextControl
                        field={{
                            label: __('Maximum Value', 'voxycure-framework'),
                            type: 'number',
                        }}
                        value={field.range_config?.max || ''}
                        onChange={(value) =>
                            handleChange('range_config', {
                                ...field.range_config,
                                max: Number(value),
                            })
                        }
                        help={__('Highest allowed value for the range slider.', 'voxycure-framework')}
                    />
                    <TextControl
                        field={{
                            label: __('Step', 'voxycure-framework'),
                            type: 'number',
                        }}
                        value={field.range_config?.step || ''}
                        onChange={(value) =>
                            handleChange('range_config', {
                                ...field.range_config,
                                step: Number(value),
                            })
                        }
                        help={__('Set the amount the slider increases or decreases with each move.', 'voxycure-framework')}
                    />
                </div>
            )}

            <ToggleControl
                label={__('Required Field', 'voxycure-framework')}
                checked={field.required || false}
                onChange={(checked) => handleChange('required', checked)}
            />

            <TextControl
                field={{
                    label: __('Default Value', 'voxycure-framework'),
                    type: 'text'
                }}
                value={field.default_value || ''}
                onChange={(value) => handleChange('default_value', value)}
            />

            <SelectControl
                field={{
                    label: __('Field Display Location', 'voxycure-framework'),
                    options: [
                        { label: __('Default (Editor Canvas)', 'voxycure-framework'), value: 'default' },
                        { label: __('Inspector Controls (Sidebar)', 'voxycure-framework'), value: 'inspector' },
                    ],
                }}
                value={field.location || 'default'}
                onChange={(value) => handleChange('location', value)}
                help={__(
                    'Choose where this field should appear when editing the block.',
                    'voxycure-framework'
                )}
            />

            <SelectControl
                field={{
                    label: __('Assign to Tab', 'voxycure-framework'),
                    options:
                        tabs.length === 0
                            ? [{ label: __('Default', 'voxycure-framework'), value: '' }]
                            : tabs.map((tab) => ({
                                label: tab.label || tab.value,
                                value: tab.value,
                            })),
                }}
                // Only use field.tab here — default is handled in field state initialization
                value={field.tab || ''}
                onChange={(value) => handleChange('tab', value)}
                help={
                    tabs.length === 0
                        ? __('No custom sections found. Using Default.', 'voxycure-framework')
                        : __('Choose which section/tab this field belongs to.', 'voxycure-framework')
                }
            />

            <Button isSmall isDestructive onClick={onRemove}>
                {__('Remove', 'voxycure-framework')}
            </Button>
        </>
    );
};

const FieldOptions = ({ options, onChange }) => (
    <BaseControl>
        <div className="field-options-repeater">
            <label style={{ fontWeight: '600', marginBottom: '0.5rem', display: 'inline-block' }}>
                {__('Options', 'voxycure-framework')}
            </label>
            {options.map((opt, idx) => (
                <div key={idx} style={{ display: 'flex', marginBottom: '8px', gap: '8px' }}>
                    <TextControl
                        field={{ type: 'text' }}
                        placeholder={__('Value', 'voxycure-framework')}
                        value={opt.value}
                        onChange={(value) => {
                            const updated = [...options];
                            updated[idx] = { ...updated[idx], value };
                            onChange(updated);
                        }}
                    />
                    <TextControl
                        field={{ type: 'text' }}
                        placeholder={__('Label', 'voxycure-framework')}
                        value={opt.label}
                        onChange={(value) => {
                            const updated = [...options];
                            updated[idx] = { ...updated[idx], label: value };
                            onChange(updated);
                        }}
                    />
                    <Button
                        isSmall
                        isDestructive
                        icon="no"
                        label={__('Remove Option', 'voxycure-framework')}
                        onClick={() => onChange(options.filter((_, i) => i !== idx))}
                    />
                </div>
            ))}
            <Button
                isSecondary
                isSmall
                icon="plus"
                onClick={() => onChange([...options, { value: '', label: '' }])}
            >
                {__('Add Option', 'voxycure-framework')}
            </Button>
        </div>
    </BaseControl>
);

export default GeneralSettingsTab;
